import {
  configureInstructorAssets,
  configurePartnerAssets,
  configureSessionAssets,
  CourseModel,
  InstructorModel,
  PartnerModel,
  SessionLightModel,
  SessionModel,
  stringToSlug,
} from '@24hbs/utils';
import { getDateOfMonth, getNext12MonthsRanges } from '$lib/utils/date';
import { getAssetsConfig } from '$lib/utils/db-utils';
import { getDurationQuery } from '$lib/utils/filters/helpers';
import { PUBLIC_PRODUCT_CODE_BLACKLIST } from '$env/static/public';

interface QueryFields {
  model?: string | string[];
  courseType?: string | string[];
  categories?: string[];
  lessionMode?: string | string[];
  duration?: number | number[];
  price?: { min?: number; max?: number };
  sort?: string;
  date?: { min?: string; max?: string };
  discount?: { min?: number; max?: number };
  month?: string[];
  credits?: string;
  instructorId?: string;
  partnerId?: string;
  certificationReleaseEntity?: string | string[];
  rankSection?: string;
  excludedProductCodes?: string[];
  promoLabels?: string[];
}

interface CloudSearchFacetBucket {
  value: string;
  count: number;
}


interface CloudSearchFacets {
  [facetName: string]: {
    buckets: CloudSearchFacetBucket[];
  };
}

export interface Facet {
  [key: string]: number;
}

export interface Facets {
  [facetName: string]: Facet;
}

const isSingle = (item: any) => typeof item === 'string' || typeof item === 'number';
const isArray = Array.isArray;

const reduceArray = (
  array: (number | string)[],
  field: string,
  operator: string = 'and',
  fn?: (item: any) => string,
  range: boolean = false
) => {
  let query = ' (' + operator;
  query += array.reduce((acc, curr) => {
    if (field.length > 0) {
      return acc + ` ${field}:${range ? '[' : "'"}${fn ? fn(curr) : curr}${range ? ']' : "'"}`;
    }
    return acc + ` ${fn ? fn(curr) : curr}`;
  }, '');
  return query + ')';
};

const createMonthsNestedQuery = (months: string | string[], useMainSessionFlag: boolean) => {
  if (!months || months.length === 0) return '';

  let query = '(or ';

  if (typeof months === 'string') {
    const [month, year] = months.split('-');
    const startDate = getDateOfMonth('start', parseInt(month), parseInt(year));
    const endDate = getDateOfMonth('end', parseInt(month), parseInt(year));
    query += ` start_date: ['${startDate}', '${endDate}']`;
    if (useMainSessionFlag) query += ` alternative_start_dates: ['${startDate}', '${endDate}']`;
  } else if (Array.isArray(months) && months.length > 0) {
    months.forEach((m) => {
      const [month, year] = m.split('-');
      const startDate = getDateOfMonth('start', parseInt(month), parseInt(year));
      const endDate = getDateOfMonth('end', parseInt(month), parseInt(year));
      query += ` start_date: ['${startDate}', '${endDate}']`;
      if (useMainSessionFlag) query += ` alternative_start_dates: ['${startDate}', '${endDate}']`;
    });
  }

  return query + ')';
};

const createNestedQuery = (
  field: string,
  item: string | string[],
  operator: string = 'and',
  applyFn?: (item: string) => string,
  range: boolean = false
) => {
  let query = '';

  if (item && item.length > 0) {
    if (typeof item === 'string') {
      query += ` ${field}: ${range ? '[' : "'"}${applyFn ? applyFn(item) : item}${range ? ']' : "'"}`;
    } else if (isArray(item)) {
      query += reduceArray(item, field, operator, applyFn, range);
    }
  }

  return query;
};

export class CloudSearch {
  public static cloudSearchItemsToPartnerModel(cloudSearchObject: any): PartnerModel | null {
    if (
      typeof cloudSearchObject === 'undefined' ||
      cloudSearchObject === null ||
      !cloudSearchObject.id ||
      !cloudSearchObject.fields ||
      !cloudSearchObject.fields.model ||
      cloudSearchObject.fields.model !== 'PartnerModel'
    ) {
      return null;
    }
    const partner = new PartnerModel();
    partner.idPartner = cloudSearchObject.fields.id_partner;
    partner.name = cloudSearchObject.fields.name;
    partner.slug = cloudSearchObject.fields.slug;
    partner.position = cloudSearchObject.fields.position;
    partner.type = cloudSearchObject.fields.type;
    partner.imageUrl = cloudSearchObject.fields.image_url;
    partner.partnerUrl = cloudSearchObject.fields.partner_url;
    partner.deleted = cloudSearchObject.fields.deleted;
    if (cloudSearchObject.fields.session_contributor && cloudSearchObject.fields.session_contributor.length > 0) {
      for (let sessionLightStr of cloudSearchObject.fields.session_contributor) {
        partner.sessionContributor.push(SessionLightModel.fromJSON(JSON.parse(sessionLightStr)));
      }
    }
    if (cloudSearchObject.fields.session_collaboration && cloudSearchObject.fields.session_collaboration.length > 0) {
      for (let sessionLightStr of cloudSearchObject.fields.session_collaboration) {
        partner.sessionCollaboration.push(SessionLightModel.fromJSON(JSON.parse(sessionLightStr)));
      }
    }

    return configurePartnerAssets(partner, getAssetsConfig());
  }

  public static cloudSearchItemsToInstructorModel(cloudSearchObject: any): InstructorModel | null {
    if (
      typeof cloudSearchObject === 'undefined' ||
      cloudSearchObject === null ||
      !cloudSearchObject.id ||
      !cloudSearchObject.fields ||
      !cloudSearchObject.fields.model ||
      cloudSearchObject.fields.model !== 'InstructorModel'
    ) {
      return null;
    }
    const instructor = new InstructorModel();
    instructor.idInstructor = cloudSearchObject.fields.id_instructor;
    instructor.name = cloudSearchObject.fields.name;
    instructor.surname = cloudSearchObject.fields.surname;
    instructor.imageUrl = cloudSearchObject.fields.image_url;
    instructor.slug = cloudSearchObject.fields.slug;
    instructor.jobTitle = cloudSearchObject.fields.job_title;
    instructor.company = cloudSearchObject.fields.company;
    instructor.description = cloudSearchObject.fields.description;
    instructor.deleted = cloudSearchObject.fields.deleted;
    instructor.updatedAt = cloudSearchObject.fields.updated_at;
    if (cloudSearchObject.fields.session_instructor && cloudSearchObject.fields.session_instructor.length > 0) {
      for (let sessionLightStr of cloudSearchObject.fields.session_instructor) {
        instructor.sessionInstructor.push(SessionLightModel.fromJSON(JSON.parse(sessionLightStr)));
      }
    }
    return configureInstructorAssets(instructor, getAssetsConfig());
  }

  public static cloudSearchItemToSessionModel(cloudSearchObject: any): SessionModel | null {
    if (
      typeof cloudSearchObject === 'undefined' ||
      cloudSearchObject === null ||
      !cloudSearchObject.id ||
      !cloudSearchObject.fields ||
      !cloudSearchObject.fields.model ||
      cloudSearchObject.fields.model !== 'SessionModel'
    ) {
      return null;
    }
    const session = new SessionModel();

    session.isMainSession = cloudSearchObject.fields.is_main_session === '1';
    session.alternativeSessions = cloudSearchObject.fields.alternative_sessions
      ? cloudSearchObject.fields.alternative_sessions.map(JSON.parse)
      : [];

    session.idSession = cloudSearchObject.fields.id_session;
    session.category = cloudSearchObject.fields.category;
    session.bannerUrl = cloudSearchObject.fields.banner_url;
    session.scholarship = cloudSearchObject.fields.scholarship === '1';
    session.brochureAvailable = cloudSearchObject.fields.brochure_available === '1';
    session.brochureUrl = cloudSearchObject.fields.brochure_url;
    session.certification = cloudSearchObject.fields.certification;
    session.internalProductCode = cloudSearchObject.fields.internal_product_code;
    session.productCode = cloudSearchObject.fields.product_code;
    session.onSaleDetail = cloudSearchObject.fields.on_sale_detail;
    session.promoDetailLabel = cloudSearchObject.fields.promo_detail_label;
    session.promoLabelStyle = cloudSearchObject.fields.promo_label_style;
    session.endDate = cloudSearchObject.fields.end_date ? new Date(Date.parse(cloudSearchObject.fields.end_date)) : null;
    session.startDate = cloudSearchObject.fields.start_date
      ? new Date(Date.parse(cloudSearchObject.fields.start_date))
      : null;
    session.audienceContent = cloudSearchObject.fields.audience_content;
    session.duration = cloudSearchObject.fields.duration ? parseInt(cloudSearchObject.fields.duration) : null;
    session.durationType = cloudSearchObject.fields.duration_type;
    session.durationDetail = cloudSearchObject.fields.duration_detail;
    session.creditDetail = cloudSearchObject.fields.credit_detail;
    session.admissionForm = cloudSearchObject.fields.admission_form === '1';
    session.courseType = cloudSearchObject.fields.course_type;
    session.admissionDetail = cloudSearchObject.fields.admissionDetail;
    session.lessionMode = cloudSearchObject.fields.lession_mode;
    session.paymentDetail = cloudSearchObject.fields.payment_detail;
    session.discountPercent = cloudSearchObject.fields.discount_percent
      ? parseInt(cloudSearchObject.fields.discount_percent)
      : 0;
    session.previewUrl = cloudSearchObject.fields.preview_url;
    session.price = cloudSearchObject.fields.price ? parseInt(cloudSearchObject.fields.price) : 0;
    session.discountPrice = cloudSearchObject.fields.discount_price ? parseInt(cloudSearchObject.fields.discount_price) : 0;
    session.priceNoVat = cloudSearchObject.fields.price_no_vat ? parseInt(cloudSearchObject.fields.price_no_vat) : 0;
    session.onSale = cloudSearchObject.fields.on_sale === '1';
    session.onSaleLabel = cloudSearchObject.fields.on_sale_label;
    session.lastSeats = cloudSearchObject.fields.last_seats === '1';
    session.location = cloudSearchObject.fields.location;
    session.courseSubType = cloudSearchObject.fields.course_sub_type;
    session.subtitle = cloudSearchObject.fields.subtitle;
    session.approved = cloudSearchObject.fields.approved === '1';
    session.courseTypeFull = cloudSearchObject.fields.course_type_full;
    session.title = cloudSearchObject.fields.title;
    session.slug = cloudSearchObject.fields.slug;
    session.buyButtonAvailable = cloudSearchObject.fields.buy_button_available === '1';
    session.soldOut = cloudSearchObject.fields.sold_out === '1';
    session.stage = cloudSearchObject.fields.stage === '1';
    session.credits_cfu = parseInt(cloudSearchObject.fields?.credits_cfu);
    session.credits_cfp = parseInt(cloudSearchObject.fields?.credits_cfp);
    session.vatFree = cloudSearchObject.fields.vat_free === '1' || cloudSearchObject.fields.vat_free === 1 ? true : false;
    session.admissionWithBuyAvailable = cloudSearchObject.fields.admission_with_buyavailable;
    session.lessonsAvailableImmediately = cloudSearchObject.fields.lessons_available_immediately === '1';
    session.hasBonus = cloudSearchObject.fields.has_bonus === '1';
    session.hasDiscount = cloudSearchObject.fields.has_discount === '1';
    session.hourDuration = cloudSearchObject.fields.hour_duration ? parseInt(cloudSearchObject.fields.hour_duration) : null;
    
    session.thematicArea = 
      cloudSearchObject.fields.thematic_area && cloudSearchObject.fields.thematic_area.length > 0
        ? [...cloudSearchObject.fields.thematic_area]
        : [];
    ;

    session.relatedSession =
      cloudSearchObject.fields.related_session && cloudSearchObject.fields.related_session.length > 0
        ? [...cloudSearchObject.fields.related_session]
        : [];
    session.relatedSessionRef =
      cloudSearchObject.fields.related_session_ref && cloudSearchObject.fields.related_session_ref.length > 0
        ? [...cloudSearchObject.fields.related_session_ref]
        : [];
    session.linkedInstructors =
      cloudSearchObject.fields.linked_instructors && cloudSearchObject.fields.linked_instructors.length > 0
        ? [...cloudSearchObject.fields.linked_instructors]
        : [];
    session.sponsorContributor =
      cloudSearchObject.fields.sponsor_contributor && cloudSearchObject.fields.sponsor_contributor.length > 0
        ? [...cloudSearchObject.fields.sponsor_contributor]
        : [];
    session.sponsorCollaboration =
      cloudSearchObject.fields.sponsor_collaboration && cloudSearchObject.fields.sponsor_collaboration.length > 0
        ? [...cloudSearchObject.fields.sponsor_collaboration]
        : [];
    session.whoyouwill = cloudSearchObject.fields.whoyouwill ? cloudSearchObject.fields.whoyouwill : '';

    if (cloudSearchObject.fields.instructors && cloudSearchObject.fields.instructors.length > 0) {
      for (let instructorStr of cloudSearchObject.fields.instructors) {
        session.instructors.push(InstructorModel.fromJSON(JSON.parse(instructorStr)));
      }
    }

    if (cloudSearchObject.fields.contributor_partners && cloudSearchObject.fields.contributor_partners.length > 0) {
      for (let partnerStr of cloudSearchObject.fields.contributor_partners) {
        session.contributorPartners.push(PartnerModel.fromJSON(JSON.parse(partnerStr)));
      }
    }

    if (cloudSearchObject.fields.collaboration_partners && cloudSearchObject.fields.collaboration_partners.length > 0) {
      for (let partnerStr of cloudSearchObject.fields.collaboration_partners) {
        session.collaborationPartners.push(PartnerModel.fromJSON(JSON.parse(partnerStr)));
      }
    }

    for (let fieldName of Object.keys(cloudSearchObject.fields)) {
      if (fieldName && fieldName.length && fieldName.length > 0 && fieldName.startsWith('tag_')) {
        session.tags[fieldName.replace('tag_', '')] = parseInt(cloudSearchObject.fields[fieldName]);
      }
    }

    return configureSessionAssets(session, getAssetsConfig());
  }

  public static parseResponse(cloudSearchObject: any): {
    courses: CourseModel[];
    total: number;
    facets?: Facets | null;
  } {
    if (!cloudSearchObject || !cloudSearchObject.hits) {
      return {
        courses: [],
        total: 0,
        facets: null,
      };
    }
    let courses: CourseModel[] = [];

    const hits = cloudSearchObject.hits;
    const facets = cloudSearchObject.facets;
    const total = hits.found;
    const dataArray = hits.hit;

    dataArray.forEach((element) => {
      if (element.fields) {
        const session: SessionModel = CloudSearch.cloudSearchItemToSessionModel(element);

        if (session) {
          const course = new CourseModel();
          course.addSession(session);
          courses.push(course);
        }
      }
    });

    return { courses, total, facets: CloudSearch.parseFacets(facets) };
  }

  public static createQuery(fields: QueryFields, useMainSessionFlag: boolean = false) {
    const model = fields.model;
    const courseType = fields.courseType;
    const categories = fields.categories;
    const lessionMode = fields.lessionMode;
    const duration = fields.duration;
    const price = fields.price;
    const date = fields.date;
    const discount = fields.discount;
    const month = fields.month;
    const credits = fields.credits;
    const instructorId = fields.instructorId;
    const partnerId = fields.partnerId;
    const certificationReleaseEntity = fields.certificationReleaseEntity;
    const rankSection = fields.rankSection;
    let promoLabels = fields.promoLabels;
    let excludedProductCodes = fields.excludedProductCodes || [];

    let query = '(and';

    query += createNestedQuery('model', model, 'or');
    if (useMainSessionFlag) query += createNestedQuery('is_main_session', '1');

    // check if courseType is an array or string
    query += createNestedQuery('course_type_full_slug', courseType, 'or', stringToSlug);
    query += createNestedQuery('lession_mode', lessionMode, 'or');
    query += createNestedQuery('categories_slugs', categories, 'or');
    query += createMonthsNestedQuery(month, useMainSessionFlag);
    query += createNestedQuery('certification_release_entity', certificationReleaseEntity, 'or');

    if (promoLabels && promoLabels.length > 0) {
      // transform all promoLabels to lowercase
      promoLabels = promoLabels.map((label) => label.toLowerCase());
      query += createNestedQuery('promo_detail_label', promoLabels, 'or');
    }

    if (duration) {
      if (isSingle(duration)) {
        query += getDurationQuery(duration as number);
      } else if (isArray(duration) && duration.length > 0) {
        query += reduceArray(duration, '', 'or', getDurationQuery);
      }
    }

    if (price && price.min && !price.max) {
      query += ` price_no_vat:[${price.min},}`;
    } else if (price && price.min && price.max) {
      query += ` price_no_vat: [${price.min}, ${price.max}]`;
    } else if (price && !price.min && price.max) {
      query += ` price_no_vat: {, ${price.max}]`;
    }

    if (date && date.min && !date.max) {
      query += ` start_date:['${date.min}',}`;
    } else if (date && date.min && date.max) {
      query += ` start_date:['${date.min}', '${date.max}']`;
    } else if (date && !date.min && date.max) {
      query += ` start_date: {, '${date.max}']`;
    }

    if (credits) {
      query += createNestedQuery(`credits_${credits}`, '1');
    }

    const isValid = (item: any) => {
      return item !== undefined && item !== null;
    };

    if (rankSection) {
      query += ` rank_section:'${rankSection}'`;
    }

    if (discount && isValid(discount.min) && !isValid(discount.max)) {
      query += ` discount_price:[${discount.min},}`;
    } else if (discount && isValid(discount.min) && isValid(discount.max)) {
      query += ` discount_price:[${discount.min}, ${discount.max}]`;
    } else if (discount && !isValid(discount.min) && isValid(discount.max)) {
      query += ` discount_price:{, ${discount.max}]`;
    }

    let productCodeBlacklist: string[] = [];
    productCodeBlacklist = PUBLIC_PRODUCT_CODE_BLACKLIST.indexOf(',') > 0 ? PUBLIC_PRODUCT_CODE_BLACKLIST.split(',') : [PUBLIC_PRODUCT_CODE_BLACKLIST];
    if (productCodeBlacklist.length > 0) {
      excludedProductCodes = [...excludedProductCodes, ...productCodeBlacklist];
    }
    
    if (excludedProductCodes.length > 0) {
      query += ` (not product_code:'${excludedProductCodes.join("') (not product_code:'")}')`;
    }

    if (instructorId) {
      query += ` (and linked_instructors:'${instructorId}')`; 
    }

    if (partnerId) {
      query += ` (or sponsor_collaboration:'${partnerId}' sponsor_contributor:'${partnerId}')`;
    }

    return query + ' )';

    /* 
    // log 
    query += ')';
    console.log("[CREATE QUERY] fields: ", fields);
    console.log("[CREATE QUERY] query: ", query);
    return query;
    //end log 
    */
  }

  public static getMonthsFacetsQuery() {
    return getNext12MonthsRanges()
      .map((monthRange) => {
        const [month, year] = monthRange.value.split('-');
        const startDate = getDateOfMonth('start', parseInt(month), parseInt(year));
        const endDate = getDateOfMonth('end', parseInt(month), parseInt(year));
        return `"['${startDate}', '${endDate}']"`;
      })
      .join(',');
  }

  private static addBuckets(...buckets: { count: number; value: string }[]): number {
    return buckets.reduce((acc, currentValue) => {
      if (currentValue) {
        return acc + currentValue.count;
      }
      return acc;
    }, 0);
  }

  private static parseFacets(facets: CloudSearchFacets): Facets | null {
    if (!facets) return null;

    const filterNonZeroFacets = (facet: Facet) =>
      Object.fromEntries(Object.entries(facet).filter(([_, count]) => count > 0));

    // course_type_full_slug
    const courseTypeFacets: Facet = filterNonZeroFacets(
      Object.fromEntries(
        facets.course_type_full_slug?.buckets.map((bucket) => [bucket.value, bucket.count]) || []
      )
    );

    // student_type
    const studentTypeFacets: Facet = filterNonZeroFacets({
      "neo-laureati": CloudSearch.addBuckets(
        facets.course_type_full_slug?.buckets.find((bucket) => bucket.value === 'master-post-laurea-con-stage') ?? { count: 0, value: '' },
        facets.course_type_full_slug?.buckets.find((bucket) => bucket.value === 'mba-full-time') ?? { count: 0, value: '' },
        facets.course_type_full_slug?.buckets.find((bucket) => bucket.value === 'corsi-online') ?? { count: 0, value: '' },
        facets.course_type_full_slug?.buckets.find((bucket) => bucket.value === 'masterclass') ?? { count: 0, value: '' }
      ),
      "neo-diplomati": CloudSearch.addBuckets(
        facets.course_type_full_slug?.buckets.find((bucket) => bucket.value === 'corsi-online') ?? { count: 0, value: '' },
        facets.course_type_full_slug?.buckets.find((bucket) => bucket.value === 'masterclass') ?? { count: 0, value: '' },
        facets.course_type_full_slug?.buckets.find((bucket) => bucket.value === 'corsi-laurea') ?? { count: 0, value: '' }
      ),
      "professionisti": CloudSearch.addBuckets(
        facets.course_type_full_slug?.buckets.find((bucket) => bucket.value === 'executive-master') ?? { count: 0, value: '' },
        facets.course_type_full_slug?.buckets.find((bucket) => bucket.value === 'professional-master') ?? { count: 0, value: '' },
        facets.course_type_full_slug?.buckets.find((bucket) => bucket.value === 'corsi-executive') ?? { count: 0, value: '' },
        facets.course_type_full_slug?.buckets.find((bucket) => bucket.value === 'executive-mba') ?? { count: 0, value: '' },
        facets.course_type_full_slug?.buckets.find((bucket) => bucket.value === 'mba-full-time') ?? { count: 0, value: '' },
        facets.course_type_full_slug?.buckets.find((bucket) => bucket.value === 'mba-part-time') ?? { count: 0, value: '' },
        facets.course_type_full_slug?.buckets.find((bucket) => bucket.value === 'corsi-online') ?? { count: 0, value: '' },
        facets.course_type_full_slug?.buckets.find((bucket) => bucket.value === 'masterclass') ?? { count: 0, value: '' }
      ),
      "executive": CloudSearch.addBuckets(
        facets.course_type_full_slug?.buckets.find((bucket) => bucket.value === 'executive-master') ?? { count: 0, value: '' },
        facets.course_type_full_slug?.buckets.find((bucket) => bucket.value === 'professional-master') ?? { count: 0, value: '' },
        facets.course_type_full_slug?.buckets.find((bucket) => bucket.value === 'corsi-executive') ?? { count: 0, value: '' },
        facets.course_type_full_slug?.buckets.find((bucket) => bucket.value === 'executive-mba') ?? { count: 0, value: '' },
        facets.course_type_full_slug?.buckets.find((bucket) => bucket.value === 'mba-part-time') ?? { count: 0, value: '' },
        facets.course_type_full_slug?.buckets.find((bucket) => bucket.value === 'corsi-online') ?? { count: 0, value: '' },
        facets.course_type_full_slug?.buckets.find((bucket) => bucket.value === 'masterclass') ?? { count: 0, value: '' }
      )
    });

    // categories_slugs
    const categoryFacets: Facet = filterNonZeroFacets(
      facets.categories_slugs?.buckets.reduce((acc, bucket) => {
        acc[bucket.value] = bucket.count || 0;
        return acc;
      }, {} as Facet) || {}
    );

    // lession_mode_slug
    const lessionModeFacets: Facet = filterNonZeroFacets({
      "live-streaming": CloudSearch.addBuckets(
        facets.lession_mode_slug?.buckets.find((bucket) => bucket.value === 'live-streaming') ?? { count: 0, value: '' },
        facets.lession_mode_slug?.buckets.find((bucket) => bucket.value === 'live-streaming-roma') ?? { count: 0, value: '' },
        facets.lession_mode_slug?.buckets.find((bucket) => bucket.value === 'live-streaming-catania') ?? { count: 0, value: '' },
        facets.lession_mode_slug?.buckets.find((bucket) => bucket.value === 'live-streaming-milano') ?? { count: 0, value: '' },
        facets.lession_mode_slug?.buckets.find((bucket) => bucket.value === 'live-streaming-on-demand') ?? { count: 0, value: '' }
      ),
      "live-streaming-milano": CloudSearch.addBuckets(
        facets.lession_mode_slug?.buckets.find((bucket) => bucket.value === 'live-streaming-milano') ?? { count: 0, value: '' }
      ),
      "live-streaming-catania": CloudSearch.addBuckets(
        facets.lession_mode_slug?.buckets.find((bucket) => bucket.value === 'live-streaming-catania') ?? { count: 0, value: '' }
      ),
      "live-streaming-roma": CloudSearch.addBuckets(
        facets.lession_mode_slug?.buckets.find((bucket) => bucket.value === 'live-streaming-roma') ?? { count: 0, value: '' },
        facets.lession_mode_slug?.buckets.find((bucket) => bucket.value === 'roma') ?? { count: 0, value: '' }
      ),
      "on-demand": CloudSearch.addBuckets(
        facets.lession_mode_slug?.buckets.find((bucket) => bucket.value === 'on-demand') ?? { count: 0, value: '' },
        facets.lession_mode_slug?.buckets.find((bucket) => bucket.value === 'live-streaming-on-demand') ?? { count: 0, value: '' }
      ),
      "live-streaming-on-demand": CloudSearch.addBuckets(
        facets.lession_mode_slug?.buckets.find((bucket) => bucket.value === 'live-streaming-on-demand') ?? { count: 0, value: '' }
      ),
      "live-streaming-milano-e-roma": CloudSearch.addBuckets(
        facets.lession_mode_slug?.buckets.find((bucket) => bucket.value === 'live-streaming-milano-e-roma') ?? { count: 0, value: '' }
      ),
      "always-on": CloudSearch.addBuckets(
        facets.lession_mode_slug?.buckets.find((bucket) => bucket.value === 'always-on') ?? { count: 0, value: '' }
      ),
    });

    // credits
    const creditsFacets: Facet = filterNonZeroFacets({
      cfu: facets?.credits_cfu?.buckets[0]?.count || 0,
      cfp: facets?.credits_cfp?.buckets[0]?.count || 0,
    });

    // month
    const monthFacets: Facet = filterNonZeroFacets(
      Object.fromEntries(
        facets.start_date?.buckets.map((bucket) => {
          const value = bucket.value;
          const count = bucket.count;

          const [date] = JSON.parse(value.replace(/'/g, '"'));
          const month = new Date(date).getMonth() + 1;
          const year = new Date(date).getFullYear();

          return [`${month}-${year}`, count];
        }) || []
      )
    );

    facets.alternative_start_dates?.buckets.forEach((bucket) => {
      const value = bucket.value;
      const count = bucket.count;
      const [date] = JSON.parse(value.replace(/'/g, '"'));
      const month = new Date(date).getMonth() + 1;
      const year = new Date(date).getFullYear();

      const key = `${month}-${year}`;
      monthFacets[key] = (monthFacets[key] || 0) + count;
    });

    return {
      course_type_full_slug: courseTypeFacets,
      student_type: studentTypeFacets,
      categories_slugs: categoryFacets,
      lession_mode_slug: lessionModeFacets,
      credits: creditsFacets,
      month: monthFacets,
    };
  }
}
